import { Link, useParams } from '@tanstack/react-router';
import { CalendarClock, CreditCard, MessageSquare, UserCog } from 'lucide-react';
import type { ReactNode } from 'react';
import { InstallmentStatusBadge, SequenceStatusBadge } from '../components/badges';
import { Facts, PageHeader } from '../components/page';
import { Empty, QueryView } from '../components/states';
import { buttonVariants } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { day, instant, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiQuery } from '../lib/query';
import { useTenant } from '../lib/referentials';
import type { InstallmentDetail } from '../lib/types';
import { useAuth } from '../lib/auth';
import { InstallmentActionsCard } from './installment-actions';

// Fiche échéance (BA-022) : données, relance en cours et chronologie complète.

const ICONS = {
  sequence: CalendarClock,
  message: MessageSquare,
  payment: CreditCard,
  audit: UserCog,
};

/** Emplacement des actions de la fiche (paiement manuel, envoi immédiat : BA-036, BA-039). */
export type InstallmentActions = (detail: InstallmentDetail) => ReactNode;

export function InstallmentDetailPage({ actions }: { actions?: InstallmentActions } = {}) {
  const { t, language } = useI18n();
  const { id } = useParams({ strict: false }) as { id: string };
  const tz = useTenant().data?.timezone;
  const query = useApiQuery<InstallmentDetail>(`/v1/installments/${id}`);
  const auth = useAuth();

  return (
    <QueryView query={query}>
      {(d) => (
        <>
          <PageHeader
            title={`${t.installments.detailTitle} ${d.contract.policyNumber}`}
            subtitle={
              <span className="flex flex-wrap items-center gap-2">
                <InstallmentStatusBadge status={d.status} />
                {d.policyholder.displayName} · {day(d.dueDate, language)}
              </span>
            }
            actions={
              <>
                <Link
                  to="/contrats/$id"
                  params={{ id: d.contract.id }}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                >
                  {t.installments.openContract}
                </Link>
                <Link
                  to="/assures/$id"
                  params={{ id: d.policyholder.id }}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                >
                  {t.installments.openPolicyholder}
                </Link>
              </>
            }
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="flex flex-col gap-4 lg:col-span-1">
              <Card>
                <CardContent>
                  <Facts
                    items={[
                      [t.installments.amountDue, money(d.amountDue, language)],
                      [t.installments.amountPaid, money(d.amountPaid, language)],
                      [t.installments.dueDate, day(d.dueDate, language)],
                      [
                        t.installments.period,
                        `${day(d.periodStart, language)} - ${day(d.periodEnd, language)}`,
                      ],
                      [t.installments.graceUntil, day(d.graceUntil, language)],
                      [t.installments.suspensionAt, day(d.suspensionAt, language)],
                      [t.installments.product, d.contract.productLabel],
                      [t.installments.agent, d.contract.agentName],
                      [t.installments.source, t.installments.sources[d.source]],
                      [
                        t.installments.sequence,
                        d.sequence ? (
                          <span className="flex flex-col gap-1">
                            <SequenceStatusBadge status={d.sequence.status} />
                            <span className="text-xs text-muted-foreground">
                              {t.installments.step} {d.sequence.currentStepNo}
                              {d.sequence.nextRunAt
                                ? ` · ${instant(d.sequence.nextRunAt, tz, language)}`
                                : ''}
                            </span>
                          </span>
                        ) : null,
                      ],
                    ]}
                  />
                </CardContent>
              </Card>
              {actions ? actions(d) : null}
              {auth.can('recordPayment') ? (
                <InstallmentActionsCard
                  installment={d}
                  onDone={() => void query.refetch()}
                  paymentOnly={!auth.can('editPortfolio')}
                />
              ) : null}
            </div>
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t.installments.timeline}</CardTitle>
              </CardHeader>
              {d.timeline.length === 0 ? (
                <Empty>{t.installments.noTimeline}</Empty>
              ) : (
                <ol className="divide-y divide-border">
                  {d.timeline.map((entry, index) => {
                    const Icon = ICONS[entry.kind];
                    return (
                      <li key={`${entry.at}-${index}`} className="flex gap-3 px-4 py-3 text-sm">
                        <Icon
                          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <p>
                            <span className="sr-only">
                              {t.installments.timelineKinds[entry.kind]} :{' '}
                            </span>
                            {entry.label}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {instant(entry.at, tz, language)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          </div>
        </>
      )}
    </QueryView>
  );
}
